import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Drawer, List, ListItemButton, ListItemIcon, ListItemText, Toolbar, Box, Typography, Avatar, Collapse } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { HugeiconsIcon } from '@hugeicons/react';
import Home01Icon from '@hugeicons/core-free-icons/dist/esm/Home01Icon';
import File01Icon from '@hugeicons/core-free-icons/dist/esm/File01Icon';
import UserIcon from '@hugeicons/core-free-icons/dist/esm/UserIcon';
import UserGroupIcon from '@hugeicons/core-free-icons/dist/esm/UserGroupIcon';
import Building01Icon from '@hugeicons/core-free-icons/dist/esm/Building01Icon';
import StarIcon from '@hugeicons/core-free-icons/dist/esm/StarIcon';
import Target01Icon from '@hugeicons/core-free-icons/dist/esm/Target01Icon';
import Mail01Icon from '@hugeicons/core-free-icons/dist/esm/Mail01Icon';
import Book01Icon from '@hugeicons/core-free-icons/dist/esm/Book01Icon';
import { useAuth } from '../context/AuthContext';
import '../styles/sidebar.css';

const drawerWidth = 240;

const Sidebar: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const handleGoProfile = () => { navigate('/profile'); };

  const isManagerOrAdmin = isAuthenticated && (user?.role === 'manager' || user?.role === 'admin');
  if (!isManagerOrAdmin) return null;

  const iconProps = { size: 20, strokeWidth: 1.8 } as const;
  
  const handleItemClick = (item: any) => {
    // Always navigate to the item's path; submenu visibility is derived from the current route
    navigate(item.path);
  };

  const items = [
    { label: 'Home', icon: <HugeiconsIcon icon={Home01Icon as any} {...iconProps} />, path: '/dashboard', enabled: true },
    { 
      label: 'Surveys', 
      icon: <HugeiconsIcon icon={File01Icon as any} {...iconProps} />, 
      path: '/surveys', 
      enabled: true,
      submenu: [
        { label: 'All Current Surveys', path: '/surveys/current', enabled: true },
        { label: 'All Completed Surveys', path: '/surveys/completed', enabled: true },
        { label: 'Survey Library', path: '/surveys/library', enabled: true },
      ]
    },
    { label: 'People', icon: <HugeiconsIcon icon={UserIcon as any} {...iconProps} />, path: '/users', enabled: true },
    { label: 'Teams', icon: <HugeiconsIcon icon={UserGroupIcon as any} {...iconProps} />, path: '/teams', enabled: true },
    { label: 'Resources', icon: <HugeiconsIcon icon={Book01Icon as any} {...iconProps} />, path: '/resources', enabled: true },
    ...(user?.role === 'admin'
      ? [
          { label: 'Organizations', icon: <HugeiconsIcon icon={Building01Icon as any} {...iconProps} />, path: '/organizations', enabled: true },
          { label: 'Community Directory', icon: <HugeiconsIcon icon={UserGroupIcon as any} {...iconProps} />, path: '/community/admin', enabled: true },
        ]
      : []),
    { label: 'Messages', icon: <HugeiconsIcon icon={Mail01Icon as any} {...iconProps} />, path: '/messages', enabled: true },
  ];

  return (
    <Drawer
      className="sg-sidebar sg-sidebar__drawer"
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: (theme: any) => ({
          width: drawerWidth,
          boxSizing: 'border-box',
          zIndex: theme.zIndex.tooltip + 1, // ensure above all
          backgroundColor: '#0f1a3a', // dark navy background
          color: '#cfd8ff',
        }),
        display: { xs: 'none', md: 'block' },
      }}
      open
    >
      {/* Sidebar Header with SuperGauge SVG logo */}
      <Toolbar className="sg-sidebar__header" sx={{ px: 2, py: 1.5 }}>
        <Box className="sg-sidebar__logo" sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>
          <Box
            component="img"
            src="/sglogo.svg"
            alt="SuperGauge"
            sx={{ height: 28, width: 'auto' }}
          />
        </Box>
      </Toolbar>
      <Box className="sg-sidebar__content" sx={{ overflow: 'auto', pb: 10 }}>
        <List className="sg-sidebar__list">
          {items.map((item) => {
            const selected = location.pathname === item.path;
            const hasSubmenuItemSelected = item.submenu?.some((subItem: any) => location.pathname === subItem.path);
            const shouldHighlight = selected || hasSubmenuItemSelected;
            // Submenus are expanded whenever their parent (or one of their submenu routes) is active
            const isExpanded = shouldHighlight;
            
            return (
              <React.Fragment key={item.label}>
                <ListItemButton
                  className={`sg-sidebar__item ${shouldHighlight ? 'sg-sidebar__item--selected' : ''}`}
                  selected={shouldHighlight}
                  disabled={!item.enabled}
                  onClick={() => item.enabled && handleItemClick(item)}
                  sx={{
                    color: shouldHighlight ? '#ffffff' : '#cfd8ff',
                    '& .MuiListItemIcon-root': { color: shouldHighlight ? '#12b8ff' : '#cfd8ff' },
                    position: 'relative',
                    py: 1.2,
                    px: 2,
                    '&.Mui-selected': {
                      backgroundColor: 'transparent',
                    },
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
                  }}
                >
                  <ListItemIcon className="sg-sidebar__icon" sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    className="sg-sidebar__label"
                    primary={item.label}
                    primaryTypographyProps={{ fontWeight: shouldHighlight ? 700 : 500 }}
                  />
                </ListItemButton>
                
                {/* Render submenu if it exists */}
                {item.submenu && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.submenu.map((subItem: any) => {
                        const subSelected = location.pathname === subItem.path;
                        return (
                          <ListItemButton
                            key={subItem.label}
                            selected={subSelected}
                            disabled={!subItem.enabled}
                            onClick={() => subItem.enabled && navigate(subItem.path)}
                            sx={{
                              pl: 4,
                              py: 1,
                              color: subSelected ? '#12b8ff' : '#9fb2ff',
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(18, 184, 255, 0.08)',
                              },
                              '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
                            }}
                          >
                            <ListItemText 
                              primary={subItem.label} 
                              primaryTypographyProps={{ 
                                fontWeight: subSelected ? 600 : 400,
                                fontSize: '0.875rem'
                              }} 
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Box>

      {/* Sidebar footer: profile avatar/name for managers/admins */}
      {isAuthenticated && user && (
        <Box className="sg-sidebar__footer" sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 2, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Box className="sg-sidebar__profile" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={handleGoProfile}>
            <Avatar className="sg-sidebar__avatar" sx={{ width: 32, height: 32, bgcolor: '#12b8ff', color: '#0f1a3a', fontWeight: 700 }}>
              {user.name?.charAt(0)?.toUpperCase?.() || 'U'}
            </Avatar>
            <Box className="sg-sidebar__user" sx={{ minWidth: 0 }}>
              <Typography className="sg-sidebar__userName" variant="body2" noWrap sx={{ color: '#ffffff' }}>{user.name}</Typography>
              <Typography className="sg-sidebar__userEmail" variant="caption" noWrap sx={{ color: '#9fb2ff' }}>{user.email}</Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default Sidebar;
